import React, { useState, useEffect, useCallback } from 'react';
import { Button, Menu } from 'antd';
import { HugeiconsIcon } from '@hugeicons/react';
import { DashboardCircleIcon, PackageIcon, CreditCardIcon, InvoiceIcon } from '@hugeicons-pro/core-bulk-rounded';
import { useLocation, useHistory } from 'react-router-dom';
import css from './SideBarProfile.module.css';
import classNames from 'classnames';
import { useCurrentUser } from '../../../context/hooks';
import logo from '../../../images/logo_96.png';
import SidebarAccountFooter from '../../../components/SidebarAccountFooter/SidebarAccountFooter';

type MenuRouteKey = 'dashboard' | 'properties' | 'billing' | 'payment';
type MenuSelection = MenuRouteKey | null;
type MenuRoutes = {
  [key in MenuRouteKey]: string;
};

type SideBarProfileProps = {
  width: number;
  onWidthChange: (width: number) => void;
  minWidth: number;
  maxWidth: number;
};

const SideBarProfile: React.FC<SideBarProfileProps> = ({ width, onWidthChange, minWidth, maxWidth }) => {
  const location = useLocation();
  const history = useHistory();
  const currentUser = useCurrentUser();
  const [selectedKey, setSelectedKey] = useState<MenuSelection>(null);
  const [isResizing, setIsResizing] = useState(false);

  const menuRoutes: MenuRoutes = {
    dashboard: '/group/dashboard',
    properties: '/group/packages',
    billing: '/group/billing',
    payment: '/group/payments',
  };

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/group/' || currentPath === '/group') {
      setSelectedKey('dashboard');
      return;
    }
    const matchedKey = (Object.keys(menuRoutes) as MenuRouteKey[]).find((key) => menuRoutes[key] && currentPath.startsWith(menuRoutes[key]));
    if (matchedKey) {
      setSelectedKey(matchedKey);
    } else {
      setSelectedKey(null);
    }
  }, [location.pathname]);

  const handleMenuClick = (key: MenuRouteKey) => {
    setSelectedKey(key);
    const route = menuRoutes[key];
    if (route) {
      history.push(route);
    }
  };

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (!onWidthChange) return;
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      const startX = e.clientX;
      const startWidth = width;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const next = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));
        onWidthChange(next);
      };

      const onMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [width, onWidthChange, minWidth, maxWidth],
  );

  return (
    <div className={classNames(css.sideBarProfile, isResizing && css.sideBarProfileResizing)} style={{ ['--sidebar-width' as string]: `${width}px` }}>
      <div className={css.sideBarProfile__scrollContent}>
        <div className={css.sideBarProfile__container}>
          {/* ── Brand card ──────────────────────────────────── */}
          <div className={css.sideBarProfile__brand}>
            <div className={css.sideBarProfile__brandLogo}>
              <img src={logo} alt="PingPad" className={css.sideBarProfile__brandLogoImg} />
            </div>
            <div className={css.sideBarProfile__brandText}>
              <div className={css.sideBarProfile__brandName}>{currentUser?.businessName || 'Account'}</div>
            </div>
          </div>

          <div className={css.sideBarProfile__menuDivider} />

          <Menu selectable={false} mode="inline" inlineIndent={12} className={css.sideBarProfile__menu}>
            <Menu.Item key="dashboard" onClick={() => handleMenuClick('dashboard')}>
              <Button type="text" className={classNames(css.sideBarProfile__menuBtn, { [css.sideBarProfile__selected]: selectedKey === 'dashboard' })}>
                <HugeiconsIcon icon={DashboardCircleIcon} size={16} color={selectedKey === 'dashboard' ? 'var(--primary-color)' : '#64748b'} />
                <span>Dashboard</span>
              </Button>
            </Menu.Item>
            <Menu.Item key="properties" onClick={() => handleMenuClick('properties')}>
              <Button type="text" className={classNames(css.sideBarProfile__menuBtn, { [css.sideBarProfile__selected]: selectedKey === 'properties' })}>
                <HugeiconsIcon icon={PackageIcon} size={16} color={selectedKey === 'properties' ? 'var(--primary-color)' : '#64748b'} />
                <span>Portfolio</span>
              </Button>
            </Menu.Item>
            <Menu.Item key="billing" onClick={() => handleMenuClick('billing')}>
              <Button type="text" className={classNames(css.sideBarProfile__menuBtn, { [css.sideBarProfile__selected]: selectedKey === 'billing' })}>
                <HugeiconsIcon icon={InvoiceIcon} size={16} color={selectedKey === 'billing' ? 'var(--primary-color)' : '#64748b'} />
                <span>Billing</span>
              </Button>
            </Menu.Item>
            <Menu.Item key="payment" onClick={() => handleMenuClick('payment')}>
              <Button type="text" className={classNames(css.sideBarProfile__menuBtn, { [css.sideBarProfile__selected]: selectedKey === 'payment' })}>
                <HugeiconsIcon icon={CreditCardIcon} size={16} color={selectedKey === 'payment' ? 'var(--primary-color)' : '#64748b'} />
                <span>Payment</span>
              </Button>
            </Menu.Item>
          </Menu>
        </div>
      </div>
      <div className={css.sideBarProfile__footerSlot}>
        <SidebarAccountFooter />
      </div>
      <div className={css.sideBarProfile__resizeLayer}>
        <div className={css.sideBarProfile__resizeHandle} onMouseDown={handleResizeStart} role="separator" aria-label="Resize sidebar" />
      </div>
    </div>
  );
};

export default SideBarProfile;
